import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  checkSlug,
  createTenant,
  listTenants,
  type Tenant,
  type TenantDeletion,
} from "../platform/tenantsApi";
import { monogram } from "../platform/tenantIdentity";
import { accessBadgeKey } from "../platform/tenantAccess";
import { deletionBannerKey, deletionSettled } from "../platform/tenantDeletion";
import { TenantDeletionsSection } from "../platform/TenantDeletionsSection";
import {
  CLASS_III_MASTERS,
  CLASS_III_MASTER_TENANT_ADMIN,
  classIiiMasterLabelKey,
} from "@web/admin/classIiiMaster";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import { formatDate } from "@web/lib/date";
import { Button, Card, Modal, TableScroll } from "@web/ui";
import { PAGE_TITLE_ID } from "@web/app/pageTitle";

// Platform-admin tenant DIRECTORY (E1, US-1.6), Verso brand. A cross-tenant list of the isolation roots:
// each row shows the tenant's slug and monogram, its status, its external-learner policy and its creation
// date, and the whole row opens that tenant's own page. The coral "New tenant" CTA opens a create dialog
// that captures the slug and confirms the isolation boundary before provisioning; slug availability is
// probed live so a collision is caught before submit (the API stays the source of truth — a race is caught
// by the 409 on submit).
//
// THE ROW IS A LINK, AND THAT IS THE WHOLE OF THIS PAGE'S JOB. It used to carry a kebab menu with five
// actions behind it — edit, provisioning, the pääkäyttäjyys handover — each opening its own dialog over
// the list, each repeating the tenant's identity inside itself. Those are now SECTIONS of
// `pages/TenantPage.tsx`, because they are facts about one customer and a directory is for finding the
// customer. What is left here is the find: a slug, a state, and a way in.
//
// A REAL LINK, stretched over the row (`.tenants__row-link`), rather than an `onClick` on the <tr>: a row
// that navigates must be reachable by Tab, openable in a new tab with the middle button, and announced as
// a link — none of which a click handler on a table row is. It also keeps the table's own semantics
// intact, which a <tr> wrapped in an anchor would not.
//
// ADR-0014 §7.3 — THE NAME AND THE LOGO ARE NOT HERE, and their absence is the decision. Both are the
// tenant's own, in a tenant-owned table this console cannot read or write: the customer's pääkäyttäjä
// sets them on their own settings page. So a customer is identified here by its SLUG, which addresses
// the tenant, is immutable and is derived from the organisation's name at creation. The alternative — a
// platform-side copy of the name — would show an operator a name the customer had already changed.
//
// ADR-0002 paths (create): a tenant appears in the list (happy); a taken slug or a missing field → a
// field validation error, nothing created (unhappy); a network drop mid-save → an error and the list is
// unchanged — no partial tenant (interrupting).
//
// ADR-0014 §7.1 / §12 C-5 — PROVISIONING is the guided operation this page's create is the first step of.
// Two things follow from "keskeneräinen provisiointi näkyy tilana":
//
//   * Every row carries a provisioning badge, from `TenantDto.provisioningComplete` on the LIST read. It is
//     on the list and not read per row from `…/provisioning` — nor from `…/tenants/{id}`, which is audited
//     for the same reason (reading one NAMED tenant is the crossing FR-Q.35 records). Either per row would
//     file an audit row for every tenant the operator scrolled past, which is also why the handover's
//     candidate read is behind a click on the tenant's own page. The LIST is the unaudited read, and this
//     directory stays on it; the FR-Q.35 guard in platform-tenant.realstack.spec.ts holds that.
//
//     Since task aa0c3eb7 the same read carries step 3's ANSWER and when it was last given, and the row
//     wears a second badge off them — because "provisiointi kesken" was also what the directory said about
//     a customer `ApplicationEntitlementGate` refuses every request from, which is not a question about
//     onboarding at all. Both facts are the platform's own, so the list stays unaudited by the same
//     argument that lets it carry `provisioningComplete`.
//   * Creating a tenant NAVIGATES to the new tenant's page, rather than leaving the operator at a list
//     with a new "incomplete" row and no indication of what is next. Step 1 is the create they just did;
//     the page they land on is where steps 2 to 4 are.

type Load = { phase: "loading" } | { phase: "error" } | { phase: "ready"; tenants: Tenant[] };

// Mirror of the backend slug rule (TenantAdminService): lowercase, 2–64 chars, hyphen-separated, no
// leading/trailing hyphen. Used only to gate the availability probe and give an early hint.
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

type SlugState =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "available"; slug: string }
  | { kind: "taken" }
  | { kind: "malformed" };

export function TenantsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [load, setLoad] = useState<Load>({ phase: "loading" });
  const [creating, setCreating] = useState(false);
  /**
   * ADR-0023 — the sentence for a deletion that just ran, said on the page the operator was sent to.
   *
   * It carries its own TONE, because the two endings are not the same news: a deletion that left an
   * external store outstanding is the normal successful path (§6.2a) and still must not wear the green of
   * a finished one — the obligation listed below it is the point. `role="status"` either way: neither is
   * an error, which is the same reading the tenant page's frozen banner has.
   */
  const [banner, setBanner] = useState<{ text: string; settled: boolean } | null>(null);

  const reload = useCallback((signal?: AbortSignal) => {
    setLoad({ phase: "loading" });
    listTenants(signal)
      .then((tenants) => setLoad({ phase: "ready", tenants }))
      .catch((err: unknown) => {
        if (signal?.aborted || err instanceof SessionExpiredError) return;
        setLoad({ phase: "error" });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => controller.abort();
  }, [reload]);

  // ADR-0023 — the deletion was performed on the tenant's own page, which stopped existing when it
  // committed, so the confirmation has to be said HERE. It is carried in the navigation state and dropped
  // from the history entry once read, so a reload of the directory does not re-announce a deletion that
  // already happened — the arrangement the create's banner uses on the tenant page.
  //
  // The sentence is chosen by STATUS and not fixed: `incomplete` is the normal successful ending (§6.2a)
  // and says something different from `done`. What is still owed is below, in the deletions section, which
  // reads it from the server rather than from this state — so an operator who reloads loses the banner
  // and keeps the obligation.
  const deleted = (location.state as { deleted?: TenantDeletion } | null)?.deleted;
  useEffect(() => {
    if (!deleted) return;
    setBanner({
      text: t(deletionBannerKey(deleted.status), { tenant: deleted.slug }),
      settled: deletionSettled(deleted.status),
    });
    navigate(location.pathname, { replace: true, state: null });
  }, [deleted, location.pathname, navigate, t]);

  // A created tenant is step 1 of four (ADR-0014 §7.1), so the operator lands on its OWN PAGE, where the
  // remaining steps are — with the success banner carried across in the navigation state rather than
  // shown on a list they are about to leave.
  const onCreated = useCallback(
    (created: Tenant) => {
      setCreating(false);
      navigate(`/tenants/${created.id}`, { state: { created: true } });
    },
    [navigate],
  );

  return (
    <section className="tenants" aria-labelledby={PAGE_TITLE_ID}>
      <header className="tenants__head">
        <p className="tenants__subtitle">{t("tenants.subtitle")}</p>
        <Button variant="primary" data-testid="create-tenant-open" onClick={() => setCreating(true)}>
          {t("tenants.newTenant")}
        </Button>
      </header>

      {banner && (
        <p
          className={`banner banner--${banner.settled ? "success" : "error"}`}
          role="status"
          data-testid="tenants-banner"
        >
          {banner.text}
        </p>
      )}

      {load.phase === "loading" && (
        <p className="empty-state" role="status" aria-live="polite">
          {t("tenants.loading")}
        </p>
      )}
      {load.phase === "error" && (
        <p className="banner banner--error" role="alert">
          {t("tenants.loadError")}
        </p>
      )}

      {load.phase === "ready" &&
        (load.tenants.length === 0 ? (
          <Card>
            <p className="empty-state">{t("tenants.empty")}</p>
          </Card>
        ) : (
          <Card flush>
            <TableScroll label={t("tenants.title")}>
              <table className="table tenants__table" data-fixed-table data-testid="tenants-table">
                {/* Declared widths: the columns must not re-measure as tenants are added or edited. */}
                <colgroup>
                  <col style={{ width: "40%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "19%" }} />
                  <col style={{ width: "5%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">{t("tenants.colTenant")}</th>
                    <th scope="col">{t("tenants.colStatus")}</th>
                    <th scope="col">{t("tenants.colExternal")}</th>
                    <th scope="col">{t("tenants.colCreated")}</th>
                    <th scope="col">
                      <span className="visually-hidden">{t("tenants.colActions")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {load.tenants.map((tenant) => (
                    <TenantRow key={tenant.id} tenant={tenant} />
                  ))}
                </tbody>
              </table>
            </TableScroll>
          </Card>
        ))}

      {/* ADR-0023 §6.3a — the demo-tenant deletions that still owe an external store, and the recently
          completed ones. BELOW the directory and not inside it: the rows name tenants that no longer
          exist, so they are not rows of this table — and they are on this page rather than on a tenant
          page because there is no tenant page left to put them on. Nothing is drawn when nobody has
          deleted a demo tenant. */}
      <TenantDeletionsSection />

      {creating && <CreateTenantDialog onClose={() => setCreating(false)} onCreated={onCreated} />}
    </section>
  );
}

function TenantRow({ tenant }: { tenant: Tenant }) {
  const { t } = useTranslation();
  const frozen = tenant.suspendedAt !== null;
  /**
   * Which shut state this customer is in, or null while it is being served (task aa0c3eb7). Read from the
   * LIST's own two fields — step 3's answer and when it was last given — through the one place this console
   * keeps the door's rule, so the row, the tenant page's header and its nav cannot name the same state
   * differently.
   */
  const shutKey = accessBadgeKey(tenant.entitlements, tenant.entitlementsRecordedAt);

  return (
    <tr data-testid="tenant-row" className="tenants__row">
      <td data-label={t("tenants.colTenant")}>
        <div className="tenants__tenant">
          {/* A monogram, never the tenant's uploaded logo: the logo lives in the tenant's own table and
              this console cannot read it (ADR-0014 §7.3). */}
          <span className="tenants__monogram" aria-hidden="true">
            {monogram(tenant.slug)}
          </span>
          <div className="tenants__identity">
            {/* The link is stretched over the whole row by `.tenants__row-link::after`, so the row is a
                click target while the ACCESSIBLE name stays just the slug. */}
            <Link
              to={`/tenants/${tenant.id}`}
              className="tenants__row-link"
              data-testid="tenant-row-link"
            >
              <code className="tenants__slug tenants__name">{tenant.slug}</code>
            </Link>
          </div>
        </div>
      </td>
      <td data-label={t("tenants.colStatus")}>
        {/* ADR-0014 §7.4 — a frozen customer is the one state an operator must be able to see from the
            directory, because it is the one that stops being a question about onboarding and starts being
            a question about why nobody at that customer can sign in. */}
        {frozen ? (
          <span className="badge badge--tenant-frozen" data-testid="tenant-status">
            {t("tenants.statusFrozen")}
          </span>
        ) : (
          <span className="badge badge--user-active" data-testid="tenant-status">
            {t("tenants.statusActive")}
          </span>
        )}
        {/* ADR-0014 §7.1 — "keskeneräinen provisiointi näkyy tilana", so it is a badge on the row and not
            something the operator has to open each tenant to find out. Only the UNFINISHED state is
            drawn: a directory where every finished customer carried a green pill would bury the few rows
            that need attention under the many that do not. */}
        {!tenant.provisioningComplete && (
          <span
            className="badge badge--status-not_started tenants__provisioning-badge"
            data-testid="tenant-provisioning"
          >
            {t("tenants.provisioning.badgeIncomplete")}
          </span>
        )}
        {/* Task aa0c3eb7 — the state this column could not say. Since `ApplicationEntitlementGate` a
            customer with no application is refused every request it makes, exactly as a frozen one is, and
            the directory showed it as "provisiointi kesken": a neutral pill about paperwork for a customer
            nobody can sign in to. It carries the FREEZE's tone, because it is the freeze's consequence, and
            the badge's own words say which of the two shut states it is — nothing recorded yet, or recorded
            without the application. Beside the provisioning badge rather than instead of it: for a tenant
            whose step 3 was never done both are true and they are different facts. */}
        {shutKey && (
          <span
            className="badge badge--tenant-frozen tenants__provisioning-badge"
            data-testid="tenant-access"
          >
            {t(shutKey)}
          </span>
        )}
      </td>
      <td data-label={t("tenants.colExternal")}>
        {tenant.allowsExternalLearners ? (
          <span className="badge badge--external-open" data-testid="tenant-external">
            {t("tenants.externalAllowed")}
          </span>
        ) : (
          <span className="badge badge--status-not_started" data-testid="tenant-external">
            {t("tenants.externalNone")}
          </span>
        )}
      </td>
      <td className="tenants__created" data-label={t("tenants.colCreated")} data-testid="tenant-created">
        {formatDate(new Date(tenant.createdAt))}
      </td>
      {/* The affordance, and nothing else: the row is already the link, so a second control here would be
          a second way to do the same thing. */}
      <td className="tenants__chevron" aria-hidden="true">
        ›
      </td>
    </tr>
  );
}

// Create dialog over the PLATFORM-owned settings: it captures the slug (with a live availability probe)
// and confirms the isolation boundary before provisioning. Cancelling (button/Escape) makes no call.
// Focus lands on the first field on open.
//
// EDITING moved to the tenant's own page (`TenantSettingsSection`), where the values are already on
// screen; this dialog is now only the create it always was underneath.
//
// There is no name field and no logo field (ADR-0014 §7.3). The form says so where the operator would
// otherwise look for them, rather than leaving them merely absent: a dialog that quietly dropped two
// fields reads as a bug, and the operator needs to know whose errand it is now.
function CreateTenantDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (created: Tenant) => void;
}) {
  const { t } = useTranslation();
  const [slug, setSlug] = useState("");
  const [allowsExternal, setAllowsExternal] = useState(false);
  // ADR-0014 §3 — the class-III master. Defaults to (a), the tenant administrator by hand, which is the
  // behaviour every tenant has today; the operator declares (b) or (c) when the customer has a feed.
  const [classIiiMaster, setClassIiiMaster] = useState<string>(CLASS_III_MASTER_TENANT_ADMIN);
  const [slugState, setSlugState] = useState<SlugState>({ kind: "idle" });
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const slugRef = useRef<HTMLInputElement>(null);

  // Live slug availability: a debounced probe once the slug is well-formed. A malformed slug is flagged
  // locally without a round-trip; the network probe is best-effort (an error leaves it un-probed).
  useEffect(() => {
    const trimmed = slug.trim().toLowerCase();
    if (trimmed.length === 0) {
      setSlugState({ kind: "idle" });
      return;
    }
    if (!SLUG_RE.test(trimmed) || trimmed.length < 2 || trimmed.length > 64) {
      setSlugState({ kind: "malformed" });
      return;
    }
    setSlugState({ kind: "checking" });
    const controller = new AbortController();
    const handle = setTimeout(() => {
      checkSlug(trimmed, controller.signal)
        .then((res) =>
          setSlugState(res.available ? { kind: "available", slug: res.slug } : { kind: "taken" }),
        )
        .catch((err: unknown) => {
          if (controller.signal.aborted || err instanceof SessionExpiredError) return;
          setSlugState({ kind: "idle" }); // probe failed — let the submit decide
        });
    }, 300);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [slug]);

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setFieldError(null);
      if (submitting) return;

      const trimmedSlug = slug.trim().toLowerCase();
      if (trimmedSlug.length === 0) {
        setFieldError(t("tenants.slugRequired"));
        return;
      }
      if (slugState.kind === "malformed") {
        setFieldError(t("tenants.slugMalformed"));
        return;
      }
      if (slugState.kind === "taken") {
        setFieldError(t("tenants.slugTaken"));
        return;
      }
      setSubmitting(true);
      createTenant({ slug: trimmedSlug, allowsExternalLearners: allowsExternal, classIiiMaster })
        .then(onCreated)
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          // 409 (slug taken) is a field-level failure; 422 is a validation failure; anything else (network
          // drop, 5xx) failed the whole create — no tenant was persisted (no partial state).
          if (err instanceof ApiError && err.status === 409) {
            setSlugState({ kind: "taken" });
            setFieldError(t("tenants.slugTaken"));
          } else if (err instanceof ApiError && err.status === 422) {
            setFieldError(t("tenants.validationError"));
          } else {
            setFieldError(t("tenants.saveError"));
          }
          setSubmitting(false);
        });
      // No `finally`: a SUCCESSFUL create navigates away, and clearing `submitting` on a dialog that is
      // being unmounted is a state update on a gone component.
    },
    [slug, allowsExternal, classIiiMaster, slugState, submitting, onCreated, t],
  );

  const titleId = "tenant-dialog-title";
  const title = t("tenants.newTenant");

  return (
    <Modal
      className="tenants__dialog"
      labelledBy={titleId}
      testId="tenant-dialog"
      onClose={onClose}
      closeDisabled={submitting}
      initialFocus={slugRef}
    >
      <h2 id={titleId}>{title}</h2>
      <form className="form" onSubmit={onSubmit} aria-label={title}>
        <label htmlFor="tenant-slug">{t("tenants.slug")}</label>
        {/* No native `required`: required-field checks are in onSubmit so the form shows the app's own
            validation message (US-1.6 unhappy path) rather than a browser tooltip. */}
        <input
          id="tenant-slug"
          ref={slugRef}
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          autoComplete="off"
          aria-describedby="tenant-slug-hint"
        />
        <p id="tenant-slug-hint" className="tenants__slug-status" aria-live="polite">
          {slugState.kind === "checking" && t("tenants.slugChecking")}
          {slugState.kind === "available" && (
            <span className="tenants__slug-ok" data-testid="slug-available">
              {t("tenants.slugAvailable")}
            </span>
          )}
          {slugState.kind === "taken" && (
            <span className="tenants__slug-bad" data-testid="slug-taken">
              {t("tenants.slugTaken")}
            </span>
          )}
          {slugState.kind === "malformed" && (
            <span className="tenants__slug-bad" data-testid="slug-malformed">
              {t("tenants.slugMalformed")}
            </span>
          )}
          {slugState.kind === "idle" && t("tenants.slugHint")}
        </p>

        {/* ADR-0014 §7.3 — where the name and the logo went, said in the place the operator would reach
            for them. Not a disabled control: the operator cannot set these at all, and a greyed-out field
            invites a support request asking why. */}
        <p className="tenants__slug-status" role="note" data-testid="tenant-owned-note">
          {t("tenants.tenantOwnedNote")}
        </p>

        <label className="tenants__check">
          <input
            type="checkbox"
            checked={allowsExternal}
            onChange={(e) => setAllowsExternal(e.target.checked)}
          />
          {t("tenants.allowsExternal")}
        </label>

        {/* ADR-0014 §3 / §7.3 — a PLATFORM-owned setting, which is why it is on this form and on none of the
            tenant's own. Choosing anything but the administrator makes the class-III fields read-only on
            every surface inside that tenant, so the hint says so before the operator commits to it. */}
        <label htmlFor="tenant-class-iii">{t("tenants.classIiiMaster")}</label>
        <select
          id="tenant-class-iii"
          value={classIiiMaster}
          onChange={(e) => setClassIiiMaster(e.target.value)}
          aria-describedby="tenant-class-iii-hint"
          data-testid="tenant-class-iii"
        >
          {CLASS_III_MASTERS.map((code) => (
            <option key={code} value={code}>
              {t(classIiiMasterLabelKey(code))}
            </option>
          ))}
        </select>
        <p id="tenant-class-iii-hint" className="tenants__slug-status">
          {classIiiMaster === CLASS_III_MASTER_TENANT_ADMIN
            ? t("tenants.classIiiHintAdmin")
            : t("tenants.classIiiHintMastered")}
        </p>

        {/* Isolation acknowledgement (US-1.6): creating a tenant provisions its own isolated data boundary. */}
        <div className="tenants__isolation" role="note" data-testid="isolation-note">
          <span className="tenants__isolation-title">{t("tenants.isolationTitle")}</span>
          <span className="tenants__isolation-body">{t("tenants.isolationBody")}</span>
        </div>

        {fieldError && (
          <p className="field-error" role="alert">
            {fieldError}
          </p>
        )}

        <div className="modal__actions">
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            {t("tenants.cancel")}
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={submitting || slugState.kind === "checking"}
            data-testid="create-tenant-submit"
          >
            {submitting ? t("tenants.creating") : t("tenants.create")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
